import { useEffect, useMemo, useRef, useState } from "react";
import { useParams } from "react-router-dom";
import { io } from "socket.io-client";
import { QRCodeSVG } from "qrcode.react";
import { getTheme } from "@/utils/themes";

const backendUrl = process.env.REACT_APP_BACKEND_URL || "";

/**
 * Big-screen "Stage View" — a read-only, dramatic projection of a live room.
 * Anyone with `/room/{code}/stage` can display it (host opens it on a projector).
 * Design pick:
 *   1b — Question-focused idle state (huge round number + WAITING FOR BUZZ, leaderboard on side)
 *   2b — Cinematic winner reveal (spotlight fade-in, name slides up with subtle glow)
 */

const formatSecondsLeft = (ms) => {
  if (ms == null) return null;
  return Math.max(0, Math.ceil(ms / 1000));
};

const Medal = ({ rank }) =>
  rank === 0 ? (
    <span className="text-2xl mr-1">🥇</span>
  ) : rank === 1 ? (
    <span className="text-2xl mr-1">🥈</span>
  ) : rank === 2 ? (
    <span className="text-2xl mr-1">🥉</span>
  ) : (
    <span className="w-6 mr-1 inline-block text-center text-[#A1A1AA] font-bold">
      {rank + 1}
    </span>
  );

/**
 * Looping confetti — pure CSS keyframes, one absolutely-positioned strip per
 * particle. Never re-renders after mount (empty deps).
 */
const CONFETTI_COLORS = ["#FF0055", "#00F0FF", "#FFEA00", "#9D4EDD", "#00FF88", "#FF7A00"];
const ConfettiRain = ({ count = 60 }) => {
  const pieces = useMemo(
    () =>
      Array.from({ length: count }).map((_, i) => ({
        left: Math.random() * 100,
        delay: -Math.random() * 6, // negative delays make particles already mid-flight on mount
        duration: 4.5 + Math.random() * 4,
        size: 8 + Math.random() * 10,
        color: CONFETTI_COLORS[i % CONFETTI_COLORS.length],
        rotate: Math.random() * 360,
        drift: (Math.random() - 0.5) * 40, // px sideways drift
      })),
    [count]
  );
  return (
    <div
      className="pointer-events-none fixed inset-0 overflow-hidden z-0"
      aria-hidden="true"
      data-testid="stage-confetti"
    >
      {pieces.map((p, i) => (
        <span
          key={i}
          className="absolute block"
          style={{
            left: `${p.left}%`,
            top: "-10vh",
            width: `${p.size}px`,
            height: `${p.size * 1.6}px`,
            background: p.color,
            borderRadius: "2px",
            transform: `rotate(${p.rotate}deg)`,
            animation: `confetti-fall ${p.duration}s linear ${p.delay}s infinite`,
            "--drift": `${p.drift}px`,
          }}
        />
      ))}
    </div>
  );
};

const StageView = () => {
  const { roomCode: rawRoomCode } = useParams();
  const roomCode = decodeURIComponent(rawRoomCode || "");

  const [state, setState] = useState(null);
  const [connected, setConnected] = useState(false);
  const [timerRemainingMs, setTimerRemainingMs] = useState(null);
  const [showJoinBox, setShowJoinBox] = useState(() => {
    // Persist visibility choice across refreshes / tabs
    return localStorage.getItem("stage_hide_qr") !== "1";
  });
  const socketRef = useRef(null);
  const timerEndRef = useRef(null); // client-side end timestamp (ms)

  // ---- Socket lifecycle ----
  useEffect(() => {
    if (!roomCode) return;
    const socket = io(backendUrl, {
      transports: ["websocket", "polling"],
      path: "/api/socket.io",
      withCredentials: true,
    });
    socketRef.current = socket;

    socket.on("connect", () => {
      setConnected(true);
      socket.emit("join_stage", { room_code: roomCode });
    });
    socket.on("disconnect", () => setConnected(false));
    socket.on("error", () => {}); // ignore — stage is best-effort

    // Merge every scoreboard-shaped payload into local state
    const applyPayload = (data) => {
      setState((prev) => ({ ...(prev || {}), ...data }));
      // Convert timer_remaining_ms (server-authoritative) into a local end timestamp
      if (typeof data.timer_remaining_ms === "number") {
        timerEndRef.current = Date.now() + data.timer_remaining_ms;
      } else if (data.timer_end_at === null) {
        timerEndRef.current = null;
      }
    };
    [
      "stage_state",
      "participant_joined",
      "participant_left",
      "buzzer_result",
      "buzzer_reset",
      "buzzer_suspended",
      "point_awarded",
      "score_adjusted",
      "round_advanced",
      "session_started",
      "game_ended",
      "game_reset",
      "theme_changed",
      "player_lock_toggled",
      "suspensions_cleared",
      "buzzer_lock_toggled",
      "timer_started",
      "timer_cancelled",
      "timer_expired",
      "round_timer_configured",
      "customization_updated",
      "player_kicked",
    ].forEach((evt) => socket.on(evt, applyPayload));

    socket.on("room_closed", () => {
      setState((prev) => ({ ...(prev || {}), _closed: true }));
    });

    return () => {
      socket.disconnect();
    };
  }, [roomCode]);

  // ---- Local timer tick (independent of server) ----
  useEffect(() => {
    const id = setInterval(() => {
      if (timerEndRef.current == null) {
        setTimerRemainingMs(null);
      } else {
        setTimerRemainingMs(Math.max(0, timerEndRef.current - Date.now()));
      }
    }, 100);
    return () => clearInterval(id);
  }, []);

  const theme = useMemo(() => getTheme(state?.theme || "default"), [state?.theme]);
  const accent = state?.custom_text_color || theme.accentColor || "#00F0FF";
  const buzzerColor = theme.buzzerColor || "#FF0055";
  const title = theme.titleColor || "#FFEA00";

  // ---- Derived: leaderboard ----
  // Team-mode: leaderboard shows TEAMS ranked by team score (individual scores hidden).
  // Solo-mode: existing behaviour — individual scores minus host.
  const rows = useMemo(() => {
    if (state?.mode === "teams") {
      const teams = state?.teams || [];
      const teamScores = state?.team_scores || {};
      return teams
        .map((t) => ({ name: t.name, score: teamScores[t.id] || 0, color: t.color }))
        .sort((a, b) => b.score - a.score);
    }
    const hostNames = new Set(
      (state?.participants || [])
        .filter((p) => p.is_host)
        .map((p) => p.name)
    );
    const scores = state?.scores || {};
    return Object.entries(scores)
      .filter(([n]) => !hostNames.has(n))
      .map(([name, score]) => ({ name, score }))
      .sort((a, b) => b.score - a.score);
  }, [state?.mode, state?.teams, state?.team_scores, state?.scores, state?.participants]);

  const customBgUrl = state?.has_custom_background
    ? `${backendUrl}/api/rooms/${encodeURIComponent(roomCode)}/background?v=${state.bg_version || 0}`
    : null;
  const customLogoUrl = state?.has_custom_logo
    ? `${backendUrl}/api/rooms/${encodeURIComponent(roomCode)}/logo?v=${state.logo_version || 0}`
    : null;

  const winner = state?.winner || null;
  const isFinished = state?.game_state === "finished";
  const secondsLeft = formatSecondsLeft(timerRemainingMs);
  const totalTimer = state?.timer_duration || null;
  const progressPct =
    timerRemainingMs != null && totalTimer
      ? Math.max(0, Math.min(100, (timerRemainingMs / (totalTimer * 1000)) * 100))
      : null;

  const joinUrl = `${window.location.origin}/join?room=${encodeURIComponent(roomCode)}`;

  // ---- Room closed sentinel ----
  if (state?._closed) {
    return (
      <div className="fixed inset-0 flex items-center justify-center bg-black text-white">
        <div className="text-center">
          <div className="text-3xl tracking-widest text-[#A1A1AA] mb-2">
            ROOM CLOSED
          </div>
          <div className="text-lg text-[#A1A1AA]">This screen is no longer live.</div>
        </div>
      </div>
    );
  }

  // ---- Not connected yet ----
  if (!state) {
    return (
      <div className="fixed inset-0 flex items-center justify-center bg-black text-white">
        <div className="text-center">
          <div
            className="w-16 h-16 rounded-full border-4 border-white/10 animate-spin mx-auto mb-6"
            style={{ borderTopColor: "#00F0FF" }}
          />
          <div className="text-sm tracking-[0.3em] text-[#A1A1AA]">
            {connected ? "LOADING STAGE…" : "CONNECTING…"}
          </div>
        </div>
      </div>
    );
  }

  // ============================================================
  // Layout: full-viewport stage
  // ============================================================
  return (
    <div
      className="fixed inset-0 overflow-y-auto landscape:overflow-hidden text-white select-none"
      style={{
        background: customBgUrl
          ? `url('${customBgUrl}') center/cover no-repeat`
          : theme.bgImage
          ? `url('${theme.bgImage}') center/cover`
          : theme.bgGradient,
        fontFamily: "Anton, sans-serif",
      }}
      data-testid="stage-view"
    >
      {/* Scrim for readability over custom backgrounds */}
      {customBgUrl && <div className="absolute inset-0 bg-black/50 pointer-events-none" />}

      {/* Looping confetti when the game has ended */}
      {state.game_state === "finished" && <ConfettiRain />}

      {/* Top strip: room title + round number */}
      <div className="relative landscape:absolute landscape:top-0 landscape:left-0 landscape:right-0 flex items-start justify-between px-6 sm:px-10 short:px-4 py-4 sm:py-6 short:py-2 z-10">
        <div className="max-w-[65%]">
          <div
            className="text-[10px] sm:text-xs short:text-[9px] tracking-[0.4em] sm:tracking-[0.5em] font-semibold mb-1"
            style={{ color: accent }}
          >
            LIVE · TAP FIRST
          </div>
          <div
            className="text-2xl sm:text-3xl landscape:sm:text-5xl short:!text-lg leading-none line-clamp-2 break-words"
            style={{ overflowWrap: "anywhere", color: "#FFFFFF" }}
            title={roomCode}
          >
            {roomCode}
          </div>
          {state.host_name && (
            <div className="text-xs sm:text-sm short:text-[10px] text-[#A1A1AA] mt-2 short:mt-0.5 tracking-widest">
              HOSTED BY {state.host_name.toUpperCase()}
            </div>
          )}
        </div>
        <div className="text-right shrink-0">
          <div
            className="text-[10px] sm:text-xs short:text-[9px] tracking-[0.4em] sm:tracking-[0.5em] font-semibold mb-1"
            style={{ color: accent }}
          >
            ROUND
          </div>
          <div
            className="text-5xl sm:text-6xl landscape:sm:text-8xl short:!text-3xl tabular-nums leading-none"
            style={{ color: title }}
          >
            {state.round_number || 1}
          </div>
        </div>
      </div>

      {/* Center stage — vertical stack of the hero content (buzzer visual / winner reveal / podium)
          plus the leaderboard directly beneath it. Portrait keeps its previous inline flow. */}
      <div
        className={`relative landscape:absolute landscape:inset-0 landscape:flex landscape:flex-col landscape:items-center landscape:justify-center landscape:gap-4 landscape:sm:gap-6 px-4 landscape:pt-32 medium:landscape:!pt-20 ${
          isFinished ? "landscape:pb-16" : "landscape:pb-40 medium:landscape:!pb-40"
        } landscape:px-8 landscape:short:!pt-12 landscape:short:!gap-3 ${
          isFinished ? "landscape:short:!pb-4" : "landscape:short:!pb-32"
        } py-8 landscape:py-0 pointer-events-none z-10`}
      >
        {state.game_state === "finished" ? (
          // ============ GAME OVER — WINNERS PODIUM ============
          (() => {
            const topThree = rows.slice(0, 3);
            const first = topThree[0];
            const second = topThree[1];
            const third = topThree[2];
            const hasAnyPoints = (first?.score || 0) > 0;
            return (
              <div className="relative w-full max-w-5xl mx-auto text-center animate-[fade-in_600ms_ease-out]">
                <div
                  className="text-[10px] sm:text-sm short:text-[9px] tracking-[0.4em] sm:tracking-[0.5em] font-semibold mb-3 sm:mb-5 short:mb-1"
                  style={{ color: accent }}
                >
                  GAME OVER
                </div>
                <div
                  className="text-5xl sm:text-7xl lg:text-8xl short:!text-4xl leading-none mb-6 sm:mb-10 short:!mb-3 break-words"
                  style={{
                    color: "#FFEA00",
                    textShadow: "0 0 30px #FFEA00, 0 0 80px #FFEA0066, 0 8px 24px rgba(0,0,0,0.7)",
                    fontFamily: "Anton, sans-serif",
                  }}
                >
                  🏆 CHAMPIONS
                </div>

                {hasAnyPoints ? (
                  <div className="flex items-end justify-center gap-3 sm:gap-6 max-w-3xl mx-auto">
                    {/* 2nd */}
                    {second && (
                      <div
                        className="flex-1 max-w-[10rem] sm:max-w-[14rem] short:max-w-[7rem] text-center animate-[rise-in_650ms_100ms_cubic-bezier(0.16,1,0.3,1)_both]"
                        data-testid="stage-podium-2"
                      >
                        <div className="text-4xl short:!text-2xl mb-2 short:mb-0.5">🥈</div>
                        <div
                          className="text-xl sm:text-2xl short:!text-sm truncate mb-2 short:mb-0.5 break-words"
                          style={{ color: "#FFFFFF", overflowWrap: "anywhere" }}
                          title={second.name}
                        >
                          {second.name}
                        </div>
                        <div
                          className="text-4xl sm:text-5xl short:!text-2xl tabular-nums"
                          style={{ color: title }}
                        >
                          {second.score}
                        </div>
                        <div
                          className="mt-3 h-24 sm:h-32 short:!h-10 rounded-t-xl"
                          style={{ background: `linear-gradient(180deg, ${accent}44, ${accent}11)`, border: `1px solid ${accent}44` }}
                        />
                      </div>
                    )}
                    {/* 1st */}
                    {first && (
                      <div
                        className="flex-1 max-w-[12rem] sm:max-w-[16rem] short:max-w-[8rem] text-center animate-[rise-in_700ms_cubic-bezier(0.16,1,0.3,1)_both]"
                        data-testid="stage-podium-1"
                      >
                        <div className="text-5xl sm:text-6xl short:!text-3xl mb-2 short:mb-0.5">🥇</div>
                        <div
                          className="text-2xl sm:text-3xl short:!text-base truncate mb-2 short:mb-0.5 break-words"
                          style={{
                            color: "#FFEA00",
                            textShadow: "0 0 15px #FFEA00",
                            overflowWrap: "anywhere",
                          }}
                          data-testid="stage-podium-1-name"
                          title={first.name}
                        >
                          {first.name}
                        </div>
                        <div
                          className="text-5xl sm:text-6xl short:!text-3xl tabular-nums"
                          style={{ color: "#FFEA00", textShadow: "0 0 20px #FFEA00aa" }}
                        >
                          {first.score}
                        </div>
                        <div
                          className="mt-3 h-32 sm:h-44 short:!h-14 rounded-t-xl"
                          style={{
                            background: "linear-gradient(180deg, #FFEA00cc, #FFEA0044)",
                            border: "1px solid #FFEA00aa",
                            boxShadow: "0 0 30px #FFEA0055",
                          }}
                        />
                      </div>
                    )}
                    {/* 3rd */}
                    {third && (
                      <div
                        className="flex-1 max-w-[10rem] sm:max-w-[14rem] short:max-w-[7rem] text-center animate-[rise-in_650ms_200ms_cubic-bezier(0.16,1,0.3,1)_both]"
                        data-testid="stage-podium-3"
                      >
                        <div className="text-4xl short:!text-2xl mb-2 short:mb-0.5">🥉</div>
                        <div
                          className="text-xl sm:text-2xl short:!text-sm truncate mb-2 short:mb-0.5 break-words"
                          style={{ color: "#FFFFFF", overflowWrap: "anywhere" }}
                          title={third.name}
                        >
                          {third.name}
                        </div>
                        <div
                          className="text-4xl sm:text-5xl short:!text-2xl tabular-nums"
                          style={{ color: title }}
                        >
                          {third.score}
                        </div>
                        <div
                          className="mt-3 h-20 sm:h-24 short:!h-8 rounded-t-xl"
                          style={{
                            background: `linear-gradient(180deg, ${buzzerColor}44, ${buzzerColor}11)`,
                            border: `1px solid ${buzzerColor}44`,
                          }}
                        />
                      </div>
                    )}
                  </div>
                ) : (
                  <div className="text-white/70 text-lg sm:text-xl mt-6">
                    No points scored this game. Ready for a rematch?
                  </div>
                )}

                <div className="mt-8 short:!mt-2 text-sm sm:text-base short:!text-[10px] text-[#A1A1AA] tracking-widest">
                  Waiting for host to start a new game…
                </div>
              </div>
            );
          })()
        ) : winner ? (
          // ============ CINEMATIC WINNER REVEAL ============
          <div
            key={`winner-${winner.timestamp}`}
            className="relative w-full max-w-5xl mx-auto text-center animate-[fade-in_450ms_ease-out]"
          >
            <div
              className="absolute inset-0 pointer-events-none animate-[spotlight_800ms_ease-out]"
              style={{
                background: `radial-gradient(ellipse at center, ${accent}22 0%, transparent 55%)`,
              }}
            />
            <div
              className="text-[10px] sm:text-sm short:!text-[9px] tracking-[0.4em] sm:tracking-[0.5em] font-semibold mb-4 sm:mb-6 short:!mb-2 animate-[fade-in_300ms_150ms_both]"
              style={{ color: accent }}
            >
              BUZZED IN
            </div>
            {/* In team mode: TEAM name is the hero (big, colored), player name is the subtitle below */}
            {state?.mode === "teams" && winner.team_name ? (
              <>
                <div
                  className="text-[14vw] landscape:sm:text-[8vw] short:!text-[7vw] leading-none px-4 sm:px-8 short:!px-2 break-words animate-[rise-in_650ms_cubic-bezier(0.16,1,0.3,1)_both]"
                  style={{
                    color: (state?.teams || []).find((t) => t.id === winner.team_id)?.color || "#FFFFFF",
                    textShadow: `0 0 40px ${accent}, 0 0 90px ${accent}66, 0 8px 24px rgba(0,0,0,0.7)`,
                    overflowWrap: "anywhere",
                  }}
                  data-testid="stage-winner-team-name"
                >
                  {winner.team_name}
                </div>
                <div
                  className="mt-2 sm:mt-4 short:!mt-1 text-2xl sm:text-4xl landscape:sm:text-[4vw] short:!text-lg text-white/90 tracking-tight animate-[fade-in_300ms_300ms_both]"
                  style={{ fontFamily: "Anton, sans-serif" }}
                  data-testid="stage-winner-name"
                >
                  {winner.name}
                </div>
              </>
            ) : (
              <div
                className="text-[14vw] landscape:sm:text-[8vw] short:!text-[7vw] leading-none px-4 sm:px-8 short:!px-2 break-words animate-[rise-in_650ms_cubic-bezier(0.16,1,0.3,1)_both]"
                style={{
                  color: "#FFFFFF",
                  textShadow: `0 0 40px ${accent}, 0 0 90px ${accent}66, 0 8px 24px rgba(0,0,0,0.7)`,
                  overflowWrap: "anywhere",
                }}
                data-testid="stage-winner-name"
              >
                {winner.name}
              </div>
            )}
            {secondsLeft != null && (
              <div className="mt-6 sm:mt-8 short:!mt-2 flex flex-col items-center animate-[fade-in_300ms_400ms_both]">
                <div
                  className="text-xs sm:text-base short:!text-[9px] tracking-[0.4em] sm:tracking-[0.5em] font-semibold mb-2 sm:mb-3 short:!mb-1"
                  style={{ color: accent }}
                >
                  TIME TO ANSWER
                </div>
                <div
                  className={`text-6xl sm:text-8xl landscape:sm:text-9xl short:!text-3xl tabular-nums transition-transform ${
                    secondsLeft <= 5 ? "text-[#FF0055] animate-pulse scale-110" : ""
                  }`}
                  style={{
                    textShadow:
                      secondsLeft <= 5
                        ? "0 0 25px #FF0055, 0 0 60px #FF005588"
                        : `0 0 20px ${accent}`,
                  }}
                >
                  {secondsLeft}s
                </div>
                {progressPct != null && (
                  <div className="w-56 sm:w-64 h-1.5 bg-white/10 rounded-full overflow-hidden mt-4">
                    <div
                      className="h-full rounded-full transition-[width] duration-100 ease-linear"
                      style={{
                        width: `${progressPct}%`,
                        background: secondsLeft <= 5 ? "#FF0055" : accent,
                      }}
                    />
                  </div>
                )}
              </div>
            )}
          </div>
        ) : (
          // ============ IDLE — QUESTION-FOCUSED WAITING STATE ============
          <div className="text-center max-w-5xl landscape:w-auto mx-auto">
            <div
              className="text-[10px] sm:text-sm short:!text-[9px] tracking-[0.4em] sm:tracking-[0.5em] font-semibold mb-6 sm:mb-8 short:!mb-2"
              style={{ color: accent }}
            >
              {state.game_state === "lobby"
                ? "WARMING UP"
                : state.buzzer_locked
                ? "BUZZERS LOCKED"
                : "WAITING FOR BUZZ"}
            </div>
            <div className="relative">
              <div
                className={`w-32 h-32 sm:w-40 sm:h-40 landscape:sm:w-[22vh] landscape:sm:h-[22vh] medium:landscape:sm:!w-[18vh] medium:landscape:sm:!h-[18vh] short:!w-20 short:!h-20 rounded-full mx-auto mb-6 sm:mb-8 landscape:sm:mb-4 short:!mb-2 relative overflow-hidden ${
                  state.game_state === "lobby" || state.buzzer_locked
                    ? ""
                    : "animate-[pulse-glow_2s_ease-in-out_infinite]"
                }`}
                style={{
                  background:
                    state.game_state === "lobby" || state.buzzer_locked
                      ? "#3F3F46"
                      : buzzerColor,
                  boxShadow:
                    state.game_state === "lobby" || state.buzzer_locked
                      ? "inset 0 -12px 24px rgba(0,0,0,0.5)"
                      : `0 0 80px ${buzzerColor}, inset 0 -12px 24px rgba(0,0,0,0.5), inset 0 12px 24px rgba(255,255,255,0.4)`,
                }}
              >
                {customLogoUrl && (
                  <img
                    src={customLogoUrl}
                    alt=""
                    className="absolute inset-0 w-full h-full object-cover opacity-55"
                  />
                )}
              </div>
            </div>
            <div
              className="text-5xl sm:text-6xl landscape:sm:text-[8vh] medium:landscape:sm:!text-[6.5vh] short:!text-3xl leading-none"
              style={{
                color: "#FFFFFF",
                textShadow: "0 4px 24px rgba(0,0,0,0.8)",
              }}
              data-testid="stage-status"
            >
              {state.game_state === "lobby"
                ? "GET READY"
                : state.buzzer_locked
                ? "HOLD ON…"
                : "TAP FIRST"}
            </div>
            <div className="text-sm sm:text-lg short:!text-[10px] text-[#A1A1AA] mt-3 sm:mt-4 landscape:sm:mt-2 short:!mt-1 tracking-widest">
              {state.game_state === "lobby"
                ? "Host will start the game any moment"
                : "The fastest hand wins the round"}
            </div>
          </div>
        )}

        {/* Leaderboard — appears directly beneath the hero content. Hidden during CHAMPIONS podium
            (the podium already shows the top 3). */}
        {!isFinished && (
          <div className="w-full max-w-md landscape:max-w-lg landscape:short:!max-w-xs medium:landscape:!max-w-sm mx-auto px-4 landscape:px-0 mb-6 landscape:mb-0 pointer-events-none">
            <div className="bg-black/50 backdrop-blur-md border border-white/10 rounded-2xl overflow-hidden">
              <div
                className="px-4 short:!px-2 py-3 short:!py-1.5 medium:landscape:!py-1.5 border-b border-white/10 flex items-center justify-between"
                style={{ background: `${accent}15` }}
              >
                <span
                  className="text-sm short:!text-[10px] medium:landscape:!text-[11px] tracking-[0.3em]"
                  style={{ color: accent }}
                >
                  LEADERBOARD
                </span>
                <span className="text-xs short:!text-[9px] medium:landscape:!text-[10px] text-[#A1A1AA]">{rows.length} players</span>
              </div>
              {rows.length ? (
                <ul className="divide-y divide-white/5">
                  {rows.slice(0, 5).map((r, idx) => {
                    const isTop = winner && winner.name === r.name;
                    return (
                      <li
                        key={r.name}
                        className={`px-4 short:!px-2 py-3 short:!py-1 medium:landscape:!py-1 flex items-center justify-between ${
                          isTop ? "bg-white/10" : ""
                        }`}
                        data-testid={`stage-leaderboard-${r.name}`}
                      >
                        <div className="flex items-center min-w-0">
                          <Medal rank={idx} />
                          <span className="text-white truncate text-lg short:!text-xs medium:landscape:!text-sm">
                            {r.name}
                          </span>
                        </div>
                        <span
                          className="tabular-nums text-2xl short:!text-base medium:landscape:!text-lg"
                          style={{ color: title }}
                        >
                          {r.score}
                        </span>
                      </li>
                    );
                  })}
                </ul>
              ) : (
                <div className="px-4 py-6 short:!py-3 text-center text-[#A1A1AA] text-sm short:!text-xs">
                  No players yet
                </div>
              )}
            </div>
          </div>
        )}
      </div>

      {/* Bottom-left — join QR + code (toggle-able, and auto-hidden when the game is finished) */}
      {showJoinBox && !isFinished && (
        <div className="relative landscape:absolute landscape:bottom-8 landscape:left-8 flex items-center justify-center landscape:justify-start gap-4 px-4 pb-6 landscape:pb-0 pointer-events-none">
          <div className="bg-white p-2 rounded-xl shrink-0">
            <QRCodeSVG value={joinUrl} size={90} level="M" fgColor="#050508" bgColor="#FFFFFF" />
          </div>
          <div className="max-w-[16rem] min-w-0">
            <div
              className="text-[10px] tracking-[0.4em] font-semibold mb-0.5"
              style={{ color: accent }}
            >
              JOIN THE GAME
            </div>
            <div className="text-white text-sm mb-1 leading-tight break-all">
              {window.location.host}/join
            </div>
            <div
              className="text-lg leading-tight break-words line-clamp-2"
              style={{ color: title, overflowWrap: "anywhere" }}
            >
              {roomCode}
            </div>
          </div>
        </div>
      )}

      {/* Toggle: show/hide QR. Fixed to viewport corner so it's always
          reachable regardless of orientation or content length. Hidden while the
          game is finished so it can't overlap the CHAMPIONS podium. */}
      {!isFinished && (
      <button
        onClick={() => {
          setShowJoinBox((v) => {
            const next = !v;
            localStorage.setItem("stage_hide_qr", next ? "0" : "1");
            return next;
          });
        }}
        className="fixed bottom-4 right-4 landscape:bottom-8 landscape:right-8 z-30 px-3 py-1.5 bg-black/60 backdrop-blur border border-white/10 rounded-full text-[10px] tracking-widest text-[#A1A1AA] hover:text-white hover:border-white/30 transition-all"
        style={{ pointerEvents: "auto" }}
        title={showJoinBox ? "Hide the join QR code" : "Show the join QR code"}
        data-testid="stage-toggle-qr"
      >
        {showJoinBox ? "🙈 HIDE QR" : "👀 SHOW QR"}
      </button>
      )}

      {/* Bottom-right — timer preset badge + connection status.
          Sits ABOVE the HIDE-QR toggle so the two never overlap. */}
      <div className="fixed bottom-14 right-4 landscape:bottom-20 landscape:right-8 flex flex-col items-end gap-2 text-xs text-[#A1A1AA] pointer-events-none z-20">
        {state.round_timer_seconds ? (
          <div
            className="bg-black/60 backdrop-blur border border-white/10 rounded-full px-3 py-1"
            data-testid="stage-timer-badge"
          >
            <span style={{ color: accent }}>⏱</span>{" "}
            {state.round_timer_seconds}s to answer
          </div>
        ) : null}
        {!connected && (
          <div className="bg-black/60 backdrop-blur border border-[#FF0055]/40 rounded-full px-3 py-1 text-[#FF0055]">
            reconnecting…
          </div>
        )}
      </div>

      {/* Local keyframes */}
      <style>{`
        @keyframes fade-in {
          from { opacity: 0 }
          to { opacity: 1 }
        }
        @keyframes rise-in {
          from { opacity: 0; transform: translateY(40px) scale(0.94) }
          to { opacity: 1; transform: translateY(0) scale(1) }
        }
        @keyframes spotlight {
          from { opacity: 0; transform: scale(0.6) }
          to { opacity: 1; transform: scale(1) }
        }
        @keyframes pulse-glow {
          0%, 100% { transform: scale(1); filter: brightness(1) }
          50% { transform: scale(1.04); filter: brightness(1.15) }
        }
        @keyframes confetti-fall {
          0%   { transform: translate3d(0, -10vh, 0) rotate(0deg); opacity: 0 }
          8%   { opacity: 1 }
          92%  { opacity: 1 }
          100% { transform: translate3d(var(--drift, 0), 110vh, 0) rotate(720deg); opacity: 0.9 }
        }
      `}</style>
    </div>
  );
};

export default StageView;
